import React, { createContext, useState, useEffect, ReactNode } from 'react';
import AuthService, { User } from '../services/auth.service';

interface AuthContextType {
  currentUser: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  updateUser: (user: User) => void;
}

const defaultAuthContext: AuthContextType = {
  currentUser: null,
  isAuthenticated: false,
  loading: true,
  login: () => Promise.reject(),
  logout: () => {},
  updateUser: () => {},
};

export const AuthContext = createContext<AuthContextType>(defaultAuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Check if user is already logged in
  useEffect(() => {
    // Check localStorage for an existing user
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setCurrentUser(parsedUser);
      } catch (e) {
        console.error("Error parsing stored user:", e);
        // Clear invalid data
        localStorage.removeItem('user');
      }
    }
    
    // Check with auth service if needed
    // const user = AuthService.getCurrentUser();
    // if (user) {
    //   setCurrentUser(user);
    // }
    
    setLoading(false);
  }, []);

  // Login function
  const login = async (email: string, password: string): Promise<User> => {
    try {
      const user = await AuthService.login({ email, password });
      setCurrentUser(user);
      return user;
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = (): void => {
    // First update the state so components stop making authenticated requests
    setCurrentUser(null);
    // Then clear localStorage
    AuthService.logout();
  };

  // Update user function
  const updateUser = (user: User): void => {
    localStorage.setItem('user', JSON.stringify(user));
    setCurrentUser(user);
  };

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    loading,
    login,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook for using the auth context
export const useAuth = (): AuthContextType => {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
